import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useCheckoutState } from '../../../../utils/checkoutState';
import { config } from '../../../../utils/config';
import useUnzer from '../../../../utils/useUnzer';
import './style.scss';

type PlanChange = {
  action: 'change-step';
  currentStep: 'plan-list' | 'plan-detail';
  planData: any[];
};

type FormDataChange = {
  action: 'validate';
  success: boolean;
};

type InstallmentEvent = PlanChange | FormDataChange;

// https://docs.unzer.com/payment-methods/unzer-installment-upl/accept-unzer-installment-ui-component/
const UnzerInstallment = () => {
  const { checkoutState } = useCheckoutState();
  const selectedPaymentOption = checkoutState.orderState.payment?.selected?.[0];
  const { isLoading, unzerInstance } = useUnzer('unzer_installment');
  const { setFieldValue } = useFormikContext<{ unzerInstallment: { isValid: boolean } }>();

  const InstallmentChangeHandler = ({ action, ...event }: InstallmentEvent) => {
    if (action === 'change-step' && 'currentStep' in event) {
      setFieldValue('unzerInstallment', {
        isValid: false,
        reason: event.currentStep === 'plan-list' ? 'plan' : 'paymentData'
      });
    }

    if (action === 'validate' && 'success' in event) {
      setFieldValue('unzerInstallment', { isValid: event.success, reason: event.success ? '' : 'paymentData' });
    }
  };

  useEffect(() => {
    if (!unzerInstance) {
      return;
    }
    const unzerElementExists =
      IS_WEB_COMPONENT && !import.meta.env.DEV && !!document.getElementById('unzer-installment-plan')?.hasChildNodes();

    if (!unzerElementExists) {
      const installment = unzerInstance.PaylaterInstallment();
      installment.create({
        containerId: 'unzer-installment-plan',
        amount: checkoutState.orderState.basket.total.costCapture / 100,
        currency: config.currency,
        country: config.country
      });
      if (!window.unzerInstallment) {
        window.unzerInstallment = installment;
      }

      setTimeout(() => {
        window.unzerInstallment?.addEventListener('paylaterInstallmentEvent', InstallmentChangeHandler);
      }, 10);
    }
  }, [unzerInstance]);

  useEffect(() => {
    if (unzerInstance) {
      window.unzerInstallment.fetchPlans({
        amount: checkoutState.orderState.basket.total.costCapture / 100,
        currency: config.currency,
        country: config.country
      });
    }
  }, [checkoutState.orderState.basket.total.costCapture]);

  useEffect(
    () => () => {
      delete window.unzerInstallment;
      setFieldValue('unzerInstallment', { isValid: false });

      if (IS_WEB_COMPONENT && !import.meta.env.DEV) {
        const unzerInstallmentsElement = document.getElementById('unzer-installment-plan');
        if (unzerInstallmentsElement) {
          unzerInstallmentsElement.innerHTML = '';
        }
      }
    },
    []
  );

  if (selectedPaymentOption?.paymentOptionKey !== 'unzer_installment' || isLoading) {
    return null;
  }

  return (
    <>
      {IS_WEB_COMPONENT && !import.meta.env.DEV ?
        <slot name="unzer-installment-container" id="unzer-installment-container" className="unzerUI form">
          <slot name="unzer-installment-plan" id="unzer-installment-plan" />
          <slot name="error-holder" id="error-holder" className="field" style={{ color: '#d0021b' }} />
        </slot>
      : <form id="unzer-installment-container" className="unzerUI form">
          <div id="unzer-installment-plan"></div>
          <div id="error-holder" className="field" style={{ color: '#d0021b' }}></div>
        </form>
      }
    </>
  );
};

export default UnzerInstallment;
